<template>
  <b-container>
    <b-modal v-model="staticImageEditVisible" title="edit static image" @ok="onModalOk" :ok-disabled="aiSubmitting" :ok-title="activeTab === 2 ? (aiSubmitting ? 'updating...' : 'update with AI') : 'OK'" size="xl" modal-class="view-editor-modal">
      <template #title>
        edit static image
        <modal-help topic="edit-static-image" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>

          <b-tab title="General" active>
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="image unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="image title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  alt text
                  <b-form-input v-model="altText" placeholder="description of the image" />
                </label>
                <small class="field-hint">Leave this blank to use the title. Visitors who cannot see the image hear this text.</small>
              </div>
            </div>
          </b-tab>

          <b-tab title="Image">
            <div class="tab-section">
              <div class="section-heading">Static Image</div>
              <p class="section-desc">upload an image to display on this view</p>

              <div v-if="previewUrl" class="static-image-preview">
                <img :src="previewUrl" alt="Preview" class="static-image-preview-img" />
                <div class="static-image-preview-actions">
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="removeImage">Remove</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="$refs.imageInput.click()">Replace</button>
                </div>
              </div>

              <div v-else class="static-image-upload-area" @click="$refs.imageInput.click()" @dragover.prevent @drop.prevent="onDrop">
                <p>Click to upload or drag and drop an image</p>
              </div>

              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onFileSelected"
              />

              <p v-if="uploading" class="upload-status">Uploading...</p>
              <p v-if="uploadError" class="upload-error">{{ uploadError }}</p>
            </div>
          </b-tab>

          <b-tab title="AI">
            <ViewAiPanel v-model="aiPrompt" :error="aiError || ''" :submitting="aiSubmitting" />
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
  </b-container>
</template>

<script>

  import axios from 'axios'
  import { submitViewAi } from '../lib/view-ai.js'
  import ViewAiPanel from './ViewAiPanel.vue'

  export default {
    name: `staticImageEdit`,
    components: {
      ViewAiPanel
    },
    data () {

      return {
        staticImageEditVisible: false,
        activeTab: 0,
        aiPrompt: ``,
        aiError: ``,
        aiSubmitting: false,
        aiSavedUniqueTitle: ``,
        uniqueTitle: ``,
        title: ``,
        altText: ``,
        staticImageId: null,
        previewUrl: null,
        uploading: false,
        uploadError: null,
        parentDesigner: null
      }
    },
    computed: {
      uploadUrl () {
        if (typeof VUE_APP_SERVER_URL !== 'undefined') {
          return `${VUE_APP_SERVER_URL}/static-images`
        }
        if (typeof VUE_APP_PROXY_SERVER_URL !== 'undefined') {
          return `${VUE_APP_PROXY_SERVER_URL}/static-images`
        }
        return '/static-images'
      }
    },
    methods: {
      showEditStaticImageComponent () {

        this.activeTab = 0
        this.aiPrompt = ''
        this.aiError = ''
        this.aiSubmitting = false
        this.aiSavedUniqueTitle = this.uniqueTitle
        this.uploadError = null

        if (this.staticImageId) {
          this.previewUrl = `${this.uploadUrl}/${this.staticImageId}`
        } else {
          this.previewUrl = null
        }

        this.staticImageEditVisible = true
      },
      onFileSelected (event) {

        const file = event.target.files[0]

        if (file) {
          this.uploadImage(file)
        }

        event.target.value = ''
      },
      onDrop (event) {

        const files = event.dataTransfer && event.dataTransfer.files

        if (files && files.length > 0) {
          const file = files[0]

          if (file.type.startsWith('image/')) {
            this.uploadImage(file)
          } else {
            this.uploadError = 'Only image files are allowed'
          }
        }
      },
      async uploadImage (file) {

        this.uploading = true
        this.uploadError = null

        const formData = new FormData()
        formData.append('file', file)

        try {

          const response = await axios.post(this.uploadUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })

          this.staticImageId = response.data.id
          this.previewUrl = `${this.uploadUrl}/${response.data.id}`
        } catch (error) {

          console.error('Failed to upload image:', error)
          this.uploadError = 'Failed to upload image'
        }

        this.uploading = false
      },
      removeImage () {

        this.staticImageId = null
        this.previewUrl = null
      },
      async onModalOk (event) {

        const handled = await submitViewAi(this, event, { visibleKey: 'staticImageEditVisible', tabIndex: 2 })

        if (!handled) return this.save()
      },
      save () {

        this.parentDesigner.saveActiveComponent({
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          altText: this.altText,
          staticImageId: this.staticImageId
        })
      }
    }
  }
</script>

<style>
.static-image-preview {
  text-align: center;
  margin: 1rem 0;
}

.static-image-preview-img {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #474842;
}

.static-image-preview-actions {
  margin-top: 0.75rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.static-image-upload-area {
  border: 2px dashed #474842;
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  color: #6a6a67;
  transition: border-color 0.15s, color 0.15s;
}

.static-image-upload-area:hover {
  border-color: #9ad12b;
  color: #b0b0ae;
}

.upload-status {
  color: #b0b0ae;
  margin-top: 0.5rem;
  font-style: italic;
}

.upload-error {
  color: #dc3545;
  margin-top: 0.5rem;
}
</style>
